/**
 * Overlay context -- full-page loading overlay triggered from any component.
 *
 * Covers the main content area (below header, above footer) with a clean
 * white background and centered spinner + message. Navigation stays visible.
 *
 * Provider is added at the App level (App.tsx). The overlay is rendered
 * once in AppShell, controlled by this context.
 *
 * @spec SPEC:ENGINEERING:FRONTEND:LAYOUT
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

interface OverlayContextValue {
  /** Show the overlay with a message. */
  showOverlay: (message: string) => void
  /** Hide the overlay. */
  hideOverlay: () => void
  /** Whether the overlay is currently visible. */
  isVisible: boolean
  /** The message to display. */
  message: string
}

const OverlayContext = createContext<OverlayContextValue | null>(null)

export function OverlayProvider({ children }: { children: ReactNode }) {
  const [isVisible, setIsVisible] = useState(false)
  const [message, setMessage] = useState('')

  const showOverlay = useCallback((msg: string) => {
    setMessage(msg)
    setIsVisible(true)
  }, [])

  const hideOverlay = useCallback(() => {
    setIsVisible(false)
    setMessage('')
  }, [])

  const value = useMemo<OverlayContextValue>(
    () => ({ showOverlay, hideOverlay, isVisible, message }),
    [showOverlay, hideOverlay, isVisible, message],
  )

  return <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>
}

export function useOverlay(): OverlayContextValue {
  const ctx = useContext(OverlayContext)
  if (!ctx) throw new Error('useOverlay must be used within OverlayProvider')
  return ctx
}
